import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/api/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
         ResponsiveContainer, ReferenceLine } from "recharts";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import { TrendingDown, DollarSign, Sun, Fuel, ArrowLeft,
         CheckCircle2, AlertTriangle, Clock, RefreshCw,
         FileText, Send, Sparkles, User } from "lucide-react";

// ── Nigeria constants ─────────────────────────────────────────────────────
const SOLAR_YIELD_KWH_PER_KWP   = 1500;  // Nigeria: ~1500 kWh/kWp/yr (high irradiation)
const DIESEL_KWH_PER_LITER      = 4.0;   // Typical large industrial generator efficiency
const CO2_PER_LITER_DIESEL      = 2.68;  // kgCO2e/liter (IPCC)

// ── Calculation engine ────────────────────────────────────────────────────
function calculate(inputs) {
  const { diesel_liters, solar_kwp, diesel_price, solar_capex_per_kwp } = inputs;
  if (!solar_kwp || solar_kwp <= 0) return null;

  const solar_output_kwh      = solar_kwp * SOLAR_YIELD_KWH_PER_KWP;
  const diesel_gen_kwh        = diesel_liters * DIESEL_KWH_PER_LITER;
  const solar_displaces_kwh   = Math.min(solar_output_kwh, diesel_gen_kwh);
  const diesel_displaced_ltr  = solar_displaces_kwh / DIESEL_KWH_PER_LITER;
  const annual_savings        = diesel_displaced_ltr * diesel_price;
  const co2_reduction         = (diesel_displaced_ltr * CO2_PER_LITER_DIESEL) / 1000; // tCO2e
  const total_capex           = solar_kwp * solar_capex_per_kwp;
  const payback               = annual_savings > 0 ? total_capex / annual_savings : Infinity;
  const roi_pct               = total_capex > 0 ? (annual_savings / total_capex) * 100 : 0;
  const diesel_replaced_pct   = diesel_liters > 0
    ? Math.min(100, (diesel_displaced_ltr / diesel_liters) * 100)
    : (solar_output_kwh / Math.max(diesel_gen_kwh, 1)) * 100;

  // Break-even chart: cumulative cash flow over 10 years
  const chart = Array.from({ length: 11 }, (_, yr) => ({
    year:       yr,
    cash_flow:  Math.round(-total_capex + annual_savings * yr),
    capex_line: Math.round(-total_capex),
  }));

  return {
    solar_output_kwh:     Math.round(solar_output_kwh),
    diesel_displaced_ltr: Math.round(diesel_displaced_ltr),
    annual_savings:       Math.round(annual_savings),
    co2_reduction:        parseFloat(co2_reduction.toFixed(2)),
    total_capex:          Math.round(total_capex),
    payback_years:        payback === Infinity ? null : parseFloat(payback.toFixed(1)),
    roi_pct:              parseFloat(roi_pct.toFixed(1)),
    diesel_replaced_pct:  parseFloat(diesel_replaced_pct.toFixed(0)),
    chart,
  };
}

// ── Payback classification ─────────────────────────────────────────────────
function getVerdict(payback_years, roi_pct) {
  if (!payback_years)
    return { label: "No savings", color: "text-slate-500", bg: "bg-slate-50 border-slate-200", icon: AlertTriangle };
  if (payback_years <= 3)
    return { label: "Strong investment — fast payback", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle2 };
  if (payback_years <= 6)
    return { label: "Viable investment — reasonable timeline", color: "text-blue-700", bg: "bg-blue-50 border-blue-200", icon: CheckCircle2 };
  return { label: "Longer payback — review capacity", color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: AlertTriangle };
}

// ── Input field ────────────────────────────────────────────────────────────
function NumericInput({ label, value, onChange, unit, help, min = 0 }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number" min={min} step="any"
          value={value === 0 ? "" : value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="flex-1 h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] tabular-nums"
          placeholder="0"
        />
        {unit && <span className="text-xs text-slate-400 whitespace-nowrap">{unit}</span>}
      </div>
      {help && <p className="text-xs text-slate-400 mt-0.5">{help}</p>}
    </div>
  );
}

// ── Result KPI card ───────────────────────────────────────────────────────
function KPICard({ label, value, unit, subtext, color, icon: Icon, bg }) {
  return (
    <MetricCard label={label} value={value} unit={unit} sub={subtext} color={color} icon={Icon} />
  );
}

// ── Break-even chart tooltip ───────────────────────────────────────────────
const BreakevenTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const cf = payload[0]?.value;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs shadow-sm">
      <p className="font-semibold text-slate-700 mb-1">Year {label}</p>
      <p className={`font-bold ${cf >= 0 ? "text-emerald-600" : "text-red-500"}`}>
        {cf >= 0 ? "+" : ""}${cf?.toLocaleString()}
      </p>
      <p className="text-slate-400">{cf >= 0 ? "Net gain" : "Outstanding investment"}</p>
    </div>
  );
};

// ── Lead Capture Card ─────────────────────────────────────────────────────
function LeadCaptureCard({ result, inputs }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name  = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await client.post("/leads/calculator", {
        ...form,
        email:   form.email.toLowerCase(),
        diesel_liters:       inputs.diesel_liters,
        electricity_kwh:     inputs.electricity_kwh,
        solar_kwp:           inputs.solar_kwp,
        diesel_price:        inputs.diesel_price,
        solar_capex_per_kwp: inputs.solar_capex_per_kwp,
        country_code:        "NG",
        annual_savings:      result.annual_savings,
        co2_reduction:       result.co2_reduction,
        payback_years:       result.payback_years,
        roi_pct:             result.roi_pct,
        source:              "calculator",
      });
      setSubmitted(true);
    } catch {
      // Silent — still mark as submitted to avoid UX friction
      setSubmitted(true);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3"
           data-testid="lead-success">
        <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">Report request received</p>
          <p className="text-xs text-emerald-700 mt-0.5">
            We'll send your personalised solar investment report to <strong>{form.email}</strong> within 24 hours.
          </p>
          <p className="text-xs text-emerald-600 mt-2">
            Summary: {inputs.solar_kwp}kWp → ${result.annual_savings?.toLocaleString()}/yr savings ·
            {result.payback_years} yr payback · {result.co2_reduction} tCO₂e/yr reduction
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-[#059669] rounded-xl overflow-hidden"
         data-testid="lead-capture-card">
      {/* CTA header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-emerald-50/30 transition-colors"
        data-testid="lead-capture-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#059669] rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText size={16} className="text-white" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-900">Get a Detailed Solar Investment Report</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Personalised PDF with full financial analysis, risk assessment and implementation roadmap
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          <span className="text-xs font-semibold text-[#059669] whitespace-nowrap">Free →</span>
        </div>
      </button>

      {/* Expandable form */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100">
          {/* What's included */}
          <div className="grid grid-cols-3 gap-2 my-4">
            {[
              { icon: "📊", label: "Full financial model" },
              { icon: "🌍", label: "Nigeria-specific data" },
              { icon: "⚡", label: "Implementation guide" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-xs text-slate-600">
                <span>{icon}</span><span>{label}</span>
              </div>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" value={form.name}
                  onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  placeholder="Sarah Okonkwo"
                  className={`w-full h-9 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] ${errors.name ? "border-red-400" : "border-slate-300"}`}
                  data-testid="lead-name-input"
                />
                {errors.name && <p className="text-xs text-red-500 mt-0.5">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                <input
                  type="text" value={form.company}
                  onChange={e => setForm(f => ({...f, company: e.target.value}))}
                  placeholder="Acme Ltd"
                  className="w-full h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
                  data-testid="lead-company-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email" value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  placeholder="sarah@company.com"
                  className={`w-full h-9 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669] ${errors.email ? "border-red-400" : "border-slate-300"}`}
                  data-testid="lead-email-input"
                />
                {errors.email && <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                <input
                  type="tel" value={form.phone}
                  onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                  placeholder="+234 8XX XXX XXXX"
                  className="w-full h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
                  data-testid="lead-phone-input"
                />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-10 bg-[#059669] text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors"
              data-testid="lead-submit-btn">
              {loading
                ? <><RefreshCw size={13} className="animate-spin" /> Sending...</>
                : <><Send size={13} /> Send Me the Report</>}
            </button>
            <p className="text-xs text-slate-400 text-center">
              No spam. Report sent within 24 hours. Unsubscribe anytime.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function NigeriaCalculator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [inputs, setInputs] = useState({
    diesel_liters:       24000,
    electricity_kwh:     185000,
    solar_kwp:           50,
    diesel_price:        0.60,
    solar_capex_per_kwp: 750,
  });

  const set = (key) => (val) => setInputs(prev => ({ ...prev, [key]: val }));

  // Load Nigeria profile for defaults
  useEffect(() => {
    client.get("/country/profiles/NG")
      .then(r => {
        setProfile(r.data);
        setInputs(prev => ({
          ...prev,
          diesel_price:        r.data.diesel_price_usd_per_liter || prev.diesel_price,
          solar_capex_per_kwp: r.data.solar_capex_usd_per_kwp    || prev.solar_capex_per_kwp,
        }));
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  // Load from assessment if passed in URL
  useEffect(() => {
    const aid = searchParams.get("assessment");
    if (!aid) return;
    client.get(`/country/assessments/${aid}/energy-breakdown`)
      .then(r => {
        if (r.data) {
          setInputs(prev => ({
            ...prev,
            diesel_liters:   r.data.diesel_liters_per_year || prev.diesel_liters,
            electricity_kwh: r.data.total_kwh              || prev.electricity_kwh,
          }));
        }
      })
      .catch(() => {});
  }, [searchParams]);

  const result = calculate(inputs);
  const verdict = result ? getVerdict(result.payback_years, result.roi_pct) : null;
  const VerdictIcon = verdict?.icon || CheckCircle2;

  const fmtUSD = (n) => n != null ? `$${n.toLocaleString()}` : "—";
  const fmtYrs = (n) => n != null ? `${n} yrs` : "—";

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans">
      <div className="px-6 py-8 space-y-6">

        {/* Header */}
        <div>
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mb-4 transition-colors">
            <ArrowLeft size={12} /> Back
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sun size={18} className="text-amber-500" />
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Nigeria Energy Intelligence
                </p>
              </div>
              <PageHeader title="Solar vs Diesel Calculator" />
              <p className="text-sm text-slate-500 mt-1">
                Quantify the investment case for solar adoption. Adjust inputs to see real-time results.
              </p>
            </div>
            {profile && (
              <div className="text-right text-xs text-slate-400">
                <p>Nigeria profile</p>
                <p>Grid: {profile.grid_emission_factor} kgCO₂e/kWh</p>
                <p>Grid reliability: {profile.avg_grid_reliability_pct}%</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Left: Inputs */}
          <div className="lg:col-span-2 space-y-4">
            {/* Energy profile */}
            <SectionCard>

              <div className="flex items-center gap-2 mb-1">
                <Fuel size={14} className="text-amber-600" />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Current Energy Profile
                </p>
              </div>
              <NumericInput
                label="Diesel Consumed"
                value={inputs.diesel_liters}
                onChange={set("diesel_liters")}
                unit="liters/yr"
                help="From your generator logs or fuel receipts"
              />
              <NumericInput
                label="Total Electricity Demand"
                value={inputs.electricity_kwh}
                onChange={set("electricity_kwh")}
                unit="kWh/yr"
                help="Grid + generator combined"
              />
                      </SectionCard>

            {/* Solar system */}
            <SectionCard>

              <div className="flex items-center gap-2 mb-1">
                <Sun size={14} className="text-amber-500" />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Proposed Solar System
                </p>
              </div>
              <NumericInput
                label="Solar Capacity"
                value={inputs.solar_kwp}
                onChange={set("solar_kwp")}
                unit="kWp"
                help={`Generates ~${(inputs.solar_kwp * SOLAR_YIELD_KWH_PER_KWP / 1000).toFixed(0)} MWh/yr in Nigeria`}
              />
              {/* Quick size buttons */}
              <div className="flex gap-2 flex-wrap">
                {[25, 50, 100, 200, 500].map(kwp => (
                  <button key={kwp} onClick={() => set("solar_kwp")(kwp)}
                    className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                      inputs.solar_kwp === kwp
                        ? "bg-[#059669] text-white border-[#059669]"
                        : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}>
                    {kwp} kWp
                  </button>
                ))}
              </div>
                      </SectionCard>

            {/* Cost assumptions */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Cost Assumptions
              </p>
              <NumericInput
                label="Diesel Price"
                value={inputs.diesel_price}
                onChange={set("diesel_price")}
                unit="$/liter"
                help="Current pump price or contract price"
              />
              <NumericInput
                label="Solar CapEx"
                value={inputs.solar_capex_per_kwp}
                onChange={set("solar_capex_per_kwp")}
                unit="$/kWp"
                help="Installed cost incl. inverter, mounting, wiring"
              />
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-slate-400 leading-relaxed px-1">
              Assumes {SOLAR_YIELD_KWH_PER_KWP} kWh/kWp/yr (Nigeria avg), {DIESEL_KWH_PER_LITER} kWh/liter diesel gen efficiency.
              Excludes O&M (~1% capex/yr), battery storage, and grid connection costs.
              Decision tool only — not a financial model.
            </p>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-3 space-y-4">
            {/* Verdict banner */}
            {result && verdict && (
              <div className={`flex items-center gap-3 p-4 rounded-xl border ${verdict.bg}`}
                   data-testid="verdict-banner">
                <VerdictIcon size={18} className={verdict.color} />
                <div>
                  <p className={`text-sm font-semibold ${verdict.color}`}>{verdict.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {result.payback_years
                      ? `${inputs.solar_kwp}kWp solar replaces ${result.diesel_replaced_pct?.toFixed(0)}% of diesel generation.`
                      : "Increase solar capacity or diesel usage to see a return."}
                  </p>
                </div>
              </div>
            )}

            {/* 4 KPI cards */}
            {result ? (
              <div className="grid grid-cols-2 gap-4" data-testid="calculator-results">
                <KPICard
                  label="Annual Savings"
                  value={fmtUSD(result.annual_savings)}
                  unit="per year"
                  subtext={`${fmtUSD(result.diesel_displaced_ltr)} diesel replaced`}
                  color="text-[#059669]"
                  bg="bg-emerald-50 border-emerald-200"
                  icon={DollarSign}
                />
                <KPICard
                  label="CO₂ Reduction"
                  value={`${result.co2_reduction}`}
                  unit="tCO₂e per year"
                  subtext={`${result.diesel_replaced_pct}% of diesel emissions`}
                  color="text-blue-700"
                  bg="bg-blue-50 border-blue-200"
                  icon={TrendingDown}
                />
                <KPICard
                  label="Payback Period"
                  value={fmtYrs(result.payback_years)}
                  unit={`on ${fmtUSD(result.total_capex)} investment`}
                  subtext={result.payback_years && result.payback_years <= 5 ? "Within typical project finance horizon" : ""}
                  color={result.payback_years && result.payback_years <= 3 ? "text-emerald-700" : result.payback_years && result.payback_years <= 6 ? "text-blue-700" : "text-amber-700"}
                  icon={Clock}
                />
                <KPICard
                  label="Simple ROI"
                  value={`${result.roi_pct}%`}
                  unit="annual return on capex"
                  subtext={result.solar_output_kwh ? `${(result.solar_output_kwh / 1000).toFixed(0)} MWh/yr generated` : ""}
                  color={result.roi_pct >= 20 ? "text-emerald-700" : "text-slate-900"}
                  icon={Sun}
                />
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <Sun size={28} className="mx-auto mb-3 text-slate-200" />
                <p className="text-sm text-slate-400">Enter solar capacity to see results</p>
              </div>
            )}

            {/* ── Lead Capture ───────────────────────────────────────────── */}
            {result && <LeadCaptureCard result={result} inputs={inputs} />}

            {/* Break-even chart */}
            {result?.chart && result.payback_years && (
              <SectionCard>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">10-Year Cash Flow</p>
                    <p className="text-xs text-slate-400">Cumulative net position after capex</p>
                  </div>
                  {result.payback_years && (
                    <span className="text-xs font-semibold text-[#059669] bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200">
                      Break-even: Year {Math.ceil(result.payback_years)}
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={result.chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#94A3B8" }}
                           axisLine={false} tickLine={false}
                           label={{ value: "Years", position: "insideBottom", offset: -2, fontSize: 10, fill: "#94A3B8" }} />
                    <YAxis tickFormatter={v => v >= 0 ? `$${(v/1000).toFixed(0)}k` : `-$${(Math.abs(v)/1000).toFixed(0)}k`}
                           tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={52} />
                    <Tooltip content={<BreakevenTooltip />} />
                    <ReferenceLine y={0} stroke="#E2E8F0" strokeWidth={1.5} />
                    <Line type="monotone" dataKey="cash_flow"
                          stroke="#059669" strokeWidth={2.5}
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            if (payload.cash_flow >= 0 && result.chart[props.index - 1]?.cash_flow < 0) {
                              return <circle key={`dot-${props.index}`} cx={cx} cy={cy} r={5} fill="#059669" stroke="white" strokeWidth={2} />;
                            }
                            return <circle key={`dot-${props.index}`} cx={cx} cy={cy} r={3} fill="#059669" />;
                          }}
                          name="Cumulative cash flow" />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Year 0: {fmtUSD(-result.total_capex)} investment → Year 10: {fmtUSD(-result.total_capex + result.annual_savings * 10)} net position
                </p>
              </SectionCard>
            )}

            {/* Sensitivity note */}
            {result && result.payback_years && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Key Sensitivities</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    {
                      label: "If diesel price +50%",
                      value: fmtUSD(Math.round(calculate({ ...inputs, diesel_price: inputs.diesel_price * 1.5 })?.annual_savings)),
                      sub: "annual savings",
                      positive: true
                    },
                    {
                      label: "If diesel price -30%",
                      value: fmtUSD(Math.round(calculate({ ...inputs, diesel_price: inputs.diesel_price * 0.7 })?.annual_savings)),
                      sub: "annual savings",
                      positive: false
                    },
                    {
                      label: "If solar capex -20%",
                      value: fmtYrs(calculate({ ...inputs, solar_capex_per_kwp: inputs.solar_capex_per_kwp * 0.8 })?.payback_years),
                      sub: "payback",
                      positive: true
                    },
                    {
                      label: "Double the capacity",
                      value: fmtUSD(Math.round(calculate({ ...inputs, solar_kwp: inputs.solar_kwp * 2 })?.annual_savings)),
                      sub: "annual savings",
                      positive: true
                    },
                  ].map(({ label, value, sub, positive }) => (
                    <div key={label} className="p-2.5 bg-white border border-slate-100 rounded-lg">
                      <p className="text-slate-400 mb-0.5">{label}</p>
                      <p className={`font-bold ${positive ? "text-emerald-600" : "text-red-600"}`}>{value}</p>
                      <p className="text-slate-400">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
